// Step + Timer — the save-worthy routine/protocol format seen across skincare
// reels (e.g. "Sheet Mask ⏱ 10-20 min", GRWM "in N steps"). A bold title, then
// numbered timed steps that pop in one per clip cut over darkened procedure
// b-roll and STAY on screen, so the finished routine is readable at the end
// (the screenshot moment).
//
// v1 design source of truth: packages/remotion/src/components/step-timer-layer.tsx
//   - chip: REEL_PALETTE.paper (rgba(250,247,242,0.95)) cream plate that HUGS
//     its text, radius 16, padding 16×28, shadow '0 8px 26px rgba(0,0,0,0.28)',
//     horizontal flex, gap 14, popped in over 10 frames
//   - chip label: "{n}. {step}" as ONE inline span, 40 / 800 / slate
//   - chip duration: 27 / 700 / 0.1em / uppercase, in the deep accent colour
//   - title: 46 / 800 / 0.08em / uppercase / white, top-centred at 8.5%
//   - checklist origin: paddingTop 26%, paddingLeft 7%, gap 18
//
// v2 build:
//   - spine: procedure b-roll under a flat dark scrim.
//   - overlay `steps`: a `group` whose root is a `repeat` over the body lines.
//     Each row is a horizontal `stack` carrying its OWN cream Frame, so the
//     plate hugs the row (v1) instead of the row being bare white text on
//     footage (the reported defect). Its children are the 1-based index badge
//     and the step copy as separate `text` nodes, so the number reads as an
//     inline prefix rather than a square badge.
//   - overlay `title`: a separate top-anchored `text` block (v1 renders the
//     title as its own persistent layer, NOT as the first checklist row).
//
// CLOSED by the `group` block (was: bare white text over footage with white
// square numbered badges — the reported defect):
//   - Every step sits on its own CREAM PLATE with DARK text: the row `stack`
//     carries a `container` Frame with v1's paper background, radius 16,
//     16×28 padding and drop shadow. Because a Frame lives on the node's own
//     placed box, the plate hugs the row instead of washing the frame.
//   - The plate HUGS: the row stack is `width: 'hug'`, so it sizes to its own
//     copy instead of stretching to the overlay's 86% column (v1's chip is
//     `alignSelf: flex-start` and only as wide as its two spans). Without it
//     every plate was a full-width band — a `stack` defaults to `fill`.
//   - The number is an INLINE PREFIX: `{ source: 'index', offset: 1,
//     suffix: '.' }` is a sibling `text` inside the row's horizontal stack,
//     rendering v1's "1." exactly (bare "1" before `suffix` existed) and
//     sharing the row's type colour — not the renderer-drawn white square badge
//     that `staggered-list.numbered` produces.
//   - THE ACTIVE-ROW ACCENT. `repeat.active` / `repeat.past` give a row a
//     TIME-VARYING look: from its own entrance the current chip flips to the
//     brand accent with `onPrimary` copy (v1 `active ? accent : paper` /
//     `accentTextColor(accent)`), and from the NEXT row's entrance it falls
//     back to a dimmed cream (v1 `dimmed` → opacity 0.8 on the finished chips).
//     The last row stays accented, which is v1's behaviour too (its
//     `activeIndex` never advances past the final reveal).
//     (Until the compiler's `mergeFrame` fix this authoring rendered WHITE ON
//     CREAM: the state's `backgroundRole` sat beside the node's literal
//     `background` and `resolveFrame` preferred the literal, so only the
//     `colorRole` half of the swap landed.)
//   - Rows pop over v1's 10 frames (`entranceDurationFrames: 10` on the row),
//     which `ListElement` had no field for.
//   - MASTER LENGTH SCALES WITH STEP COUNT AGAIN. `duration: { kind: 'driven',
//     by: 'steps' }` now resolves: `blockRegistry.group.computeContentDuration`
//     finds the `repeat` in the tree and sizes it off `stagger.beatsPerItem` ×
//     items, the SAME arithmetic a staggered-list uses — ONE window per step
//     and no tail, matching v1's `steps.length * 2.6s`. The driver block must
//     therefore be `duration: { kind: 'content' }` — a `fill` driver is
//     rejected, since fill needs a master to size against. At the registry's
//     nominal 100bpm that is 4 beats × 18 frames = 72 frames per step:
//     3 steps → 216 frames (7.2s), 6 steps → 432 frames (14.4s).
//
// FIDELITY GAPS vs v1 that REMAIN:
//   - TWO-TONE NAME / DURATION IS NOT REACHABLE. v1 draws the step name and its
//     duration as two spans (40/800 slate vs 27/700 uppercase deep-accent), but
//     v2's script carries each step as ONE body string ("Cleanse — 60 sec").
//     `group` can express the two text nodes; the COPY cannot supply two
//     fields, so the row renders name+duration as a single dark span. This
//     needs declared per-item content fields, not a `group` change.
//   - THE DIM IS A COLOUR, NOT AN OPACITY. v1 dims a finished chip by dropping
//     the whole element to `opacity: 0.8`. Neither `Frame` nor
//     `TypeStyleOverride` carries opacity, so `past` re-states the paper
//     background and the slate copy at the equivalent alpha instead. Close in
//     appearance; it does NOT dim the plate's shadow the way v1 does.
//   - TEXT-ON-ACCENT CONTRAST PICKING. v1 runs `accentTextColor(accent)` so a
//     pale brand accent gets dark copy. `colorRole` binds to fixed theme roles
//     (there is no `onAccent` and no derived colour), so the active chip is
//     pinned to `onPrimary` — the same gap poll-1 documents.
//     For the engine-default accent #0066FF the two AGREE: v1's luminance
//     (0.299/0.587/0.114) scores it 88.9 ≤ 150 → '#FFFFFF', which is exactly
//     `onPrimary`, at a 4.83:1 contrast ratio. The pairing only diverges for a
//     LIGHT brand accent (v1 luminance > 150), where v1 flips to slate and v2
//     would still write `onPrimary` white. That is the residual risk; it is a
//     THEME-level gap (an `onAccent` role, or a derived contrast colour), not
//     something a literal in this template could fix without hard-coding a
//     colour that ignores the brand accent entirely.
//   - The chip pop is the `pop` token, not v1's spring (damping 14 /
//     stiffness 160 / mass 0.7) scaled from `left center`.
//   - v1's baseline alignment between the two spans, and its `maxWidth: 86%`
//     per chip, have no representation (`align: 'end'` is the closest cross-axis
//     match; the 86% cap is approximated by the overlay placement width).
//   - The b-roll cuts stay `beat-synced` at the row stagger (4 beats) rather
//     than `overlay-synced`. `overlay-synced` now accepts a `group` as its
//     driver, but only one containing a SEQUENTIAL `repeat` — an accumulating
//     checklist has no statement windows to cut to, and it THROWS rather than
//     silently no-opping, so authoring it here would break the render.
export const stepTimer1 = {
    id: 'step-timer-1',
    schemaVersion: 2,
    aspectRatios: ['portrait'],
    // Scales with the step count — see the `steps` group below, which is the
    // driver and must therefore be `content`.
    duration: { kind: 'driven', by: 'steps' },
    root: {
        kind: 'leaf',
        id: 'main',
        spine: [
            {
                kind: 'media-track',
                id: 'broll',
                duration: { kind: 'fill' },
                clips: {
                    source: 'query',
                    query: { kind: 'asset-clips', tag: 'procedure', count: [1, 4] },
                    required: true,
                },
                fit: 'contain-blur',
                // Same 4-beat cadence as the row stagger, so each step pops on a cut
                // (v1 computes reveals from the b-roll scene starts).
                cuts: { mode: 'beat-synced', beatsPerEdit: 4 },
                fillMode: 'loop',
            },
            // Dark scrim so the title stays legible over footage; the rows carry
            // their own cream plates.
            {
                kind: 'solid',
                id: 'scrim',
                duration: { kind: 'fill' },
                color: { source: 'fixed', value: 'rgba(0,0,0,0.42)' },
            },
        ],
        overlays: [
            // The building checklist. One `repeat` over the step lines; each row is
            // its own plated horizontal stack, and rows accumulate down the frame.
            {
                kind: 'group',
                id: 'steps',
                // `content` — NOT `fill`. This block drives the master (see the doc's
                // `duration` above) and `resolveMasterFrames` rejects a `fill` driver.
                duration: { kind: 'content' },
                // v1 stacked the checklist from 26% down the frame, left-aligned at 7%.
                placement: { anchor: 'top-left', x: 0.07, y: 0.26, width: 0.86 },
                root: {
                    kind: 'repeat',
                    over: {
                        source: 'query',
                        query: { kind: 'script-text', role: 'body' },
                        required: true,
                    },
                    direction: 'v',
                    // v1 checklist gap.
                    gap: 18,
                    // Plates hug their copy and stack flush-left.
                    align: 'start',
                    // One step per clip cut; `accumulate` keeps the finished routine on
                    // screen (v1's persistent checklist).
                    stagger: { beatsPerItem: 4 },
                    reveal: 'accumulate',
                    // v1's active/dimmed chip states, in time. `active` runs from a row's
                    // own entrance until the next row's; `past` from that point on. The
                    // last row never goes past, matching v1's `activeIndex`.
                    active: {
                        container: {
                            // v1: `backgroundColor: active ? accent : REEL_PALETTE.paper`.
                            backgroundRole: 'accent',
                        },
                        // v1: `accentTextColor(accent)`; `onPrimary` is v2's nearest role.
                        styleOverride: { colorRole: 'onPrimary' },
                    },
                    past: {
                        // v1 dims the FINISHED chip to opacity 0.8; with no opacity knob,
                        // the paper and slate are restated at the equivalent alpha.
                        container: { background: 'rgba(250,247,242,0.76)' },
                        styleOverride: { color: 'rgba(21,22,28,0.8)' },
                    },
                    child: {
                        kind: 'stack',
                        direction: 'h',
                        // The plate HUGS its copy (v1 `alignSelf: 'flex-start'`); the
                        // default `fill` stretched it across the whole 86% column.
                        width: 'hug',
                        // v1 gap between the label span and the duration span is 14; the
                        // number is an inline prefix inside the label span there, so a
                        // single uniform gap is the closest v2 can get (see gaps above).
                        gap: 12,
                        // v1 aligns the two spans on their baseline; 'end' is the nearest
                        // cross-axis match available to a stack.
                        align: 'end',
                        container: {
                            // REEL_PALETTE.paper — the cream plate.
                            background: 'rgba(250,247,242,0.95)',
                            radius: 16,
                            paddingX: 28,
                            paddingY: 16,
                            shadow: '0 8px 26px rgba(0,0,0,0.28)',
                        },
                        entrance: 'pop',
                        entranceDurationFrames: 10,
                        children: [
                            {
                                // The 1-based inline number — a sibling text node, NOT a
                                // renderer-drawn badge. `suffix` gives v1's "1." exactly.
                                kind: 'text',
                                content: { source: 'index', offset: 1, suffix: '.' },
                                style: 'heading',
                                styleOverride: {
                                    fontSize: 40,
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    // REEL_PALETTE.slate — dark on the cream plate.
                                    color: '#15161C',
                                    // v1 label span.
                                    lineHeight: 1.2,
                                    strokeWidth: 0,
                                },
                                align: 'left',
                            },
                            {
                                // "Cleanse — 60 sec": one string, because the script has one
                                // field per step (see the two-tone gap above).
                                kind: 'text',
                                content: { source: 'item' },
                                style: 'heading',
                                styleOverride: {
                                    fontSize: 40,
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    color: '#15161C',
                                    // v1 label span.
                                    lineHeight: 1.2,
                                    strokeWidth: 0,
                                },
                                align: 'left',
                            },
                        ],
                    },
                },
            },
            // The routine title. In v1 this is a SEPARATE persistent block pinned to
            // the top of the frame (step-timer-layer.tsx:162-188, paddingTop 8.5%),
            // NOT the first row of the checklist. Authoring it as a list `lead` made
            // it one element of a centred column, so it rendered mid-screen — an
            // earlier reported defect. Left as its own top-anchored text overlay.
            {
                kind: 'text',
                id: 'title',
                text: {
                    source: 'query',
                    query: { kind: 'script-text', role: 'hook' },
                    required: true,
                },
                style: 'heading',
                styleOverride: {
                    fontSize: 46,
                    fontWeight: 800,
                    letterSpacing: 0.08,
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    // v1 title block.
                    lineHeight: 1.14,
                    textShadow: '0 3px 20px rgba(0,0,0,0.55)',
                    strokeWidth: 0,
                },
                animation: { entrance: 'fade-in', entranceDurationFrames: 12 },
                placement: { anchor: 'top', x: 0.5, y: 0.085, width: 0.84 },
                duration: { kind: 'fill' },
                container: 'none',
            },
        ],
    },
    globals: {
        audio: {
            music: { source: 'query', query: { kind: 'music' }, required: false },
        },
    },
};
//# sourceMappingURL=step-timer-1.js.map